import { ValidationService } from 'aspnet-client-validation';
import { POSTCODE_REQUIRED_RULE, isPostcodeValid } from './postcode-rule';

export class CustomValidationService {
    private validationService: ValidationService;

    constructor() {
        this.validationService = new ValidationService();
    }

    public initialize(): void {
        this.validationService.bootstrap();
        this.setupCustomProviders();

        // jQuery Validate adapter registration must be deferred until DOM is ready
        // because jQuery itself is loaded via a script tag, not bundled
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupJQueryAdapters());
        } else {
            this.setupJQueryAdapters();
        }
    }

    private setupJQueryAdapters(): void {
        const $: any = (window as any).$;
        if (!$ || !$.validator || !$.validator.unobtrusive) return;

        // Register the requiredif validation method so jQuery Validate understands
        // fields decorated with [RequiredIf] when $form.valid() is called directly
        $.validator.addMethod('requiredif', function (value: string, _element: HTMLElement, params: any) {
            const other = document.querySelector<HTMLInputElement>('#' + params.otherproperty);
            if (!other) return true;

            const otherValue = other.type === 'checkbox'
                ? other.checked.toString()
                : other.value;

            if (otherValue.toLowerCase() === (params.otherpropertyvalue ?? '').toLowerCase()) {
                return value !== null && value !== undefined && value.trim() !== '';
            }

            return true;
        }, '');  // message comes from the unobtrusive adapter

        $.validator.unobtrusive.adapters.add('requiredif', ['otherproperty', 'otherpropertyvalue'], function (options: any) {
            options.rules['requiredif'] = {
                otherproperty: options.params.otherproperty,
                otherpropertyvalue: options.params.otherpropertyvalue
            };
            options.messages['requiredif'] = options.message;
        });

        // [PostcodeRequiredForCountry] — must match the aspnet-client-validation provider below,
        // as both libraries independently block submission
        $.validator.addMethod(POSTCODE_REQUIRED_RULE, function (_value: string, element: HTMLInputElement, params: any) {
            return isPostcodeValid(element, params.countryproperty, params.countries);
        }, '');

        $.validator.unobtrusive.adapters.add(POSTCODE_REQUIRED_RULE, ['countryproperty', 'countries'], function (options: any) {
            options.rules[POSTCODE_REQUIRED_RULE] = {
                countryproperty: options.params.countryproperty,
                countries: options.params.countries
            };
            options.messages[POSTCODE_REQUIRED_RULE] = options.message;
        });

        this.patchDateRangeMessages($);
    }

    /**
     * jQuery Validate auto-generates 'min'/'max' rules from native HTML5 min/max attributes
     * on any input (e.g. our <input type="date" min="..." max="...">) independently of
     * data-val-* — its default messages embed the raw ISO date (e.g. "2026-09-03") rather
     * than British dd/mm/yyyy. Reformat the message for date inputs only; other field types
     * (e.g. numeric ranges) keep jQuery Validate's normal default wording.
     */
    private patchDateRangeMessages($: any): void {
        const formatDateUk = (isoDate: string): string => {
            const [year, month, day] = isoDate.split('-');
            return `${day}/${month}/${year}`;
        };

        const originalMax = $.validator.messages.max;
        const originalMin = $.validator.messages.min;

        $.validator.messages.max = function (param: string, element: HTMLInputElement) {
            if (element.type === 'date') {
                return `Please enter a date on or before ${formatDateUk(param)}.`;
            }
            return typeof originalMax === 'function' ? originalMax(param, element) : $.validator.format(originalMax)(param);
        };

        $.validator.messages.min = function (param: string, element: HTMLInputElement) {
            if (element.type === 'date') {
                return `Please enter a date on or after ${formatDateUk(param)}.`;
            }
            return typeof originalMin === 'function' ? originalMin(param, element) : $.validator.format(originalMin)(param);
        };
    }

    private setupCustomProviders(): void {
        this.setupMinimumAgeProvider();
        this.setupRequiredIfProvider();
        this.setupPostcodeRequiredProvider();
    }

    private setupPostcodeRequiredProvider(): void {
        this.validationService.addProvider(POSTCODE_REQUIRED_RULE, (_value, element, params) => {
            return isPostcodeValid(element as HTMLInputElement, params.countryproperty, params.countries);
        });
    }

    private setupMinimumAgeProvider(): void {
        this.validationService.addProvider('minimumage', (value, _element, params) => {
            let date = new Date(value);
            let today = new Date();

            let minimumAge = parseInt(params['age']) ?? 18;
            let age = today.getFullYear() - date.getFullYear();
            let monthDiff = today.getMonth() - date.getMonth();

            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
                age--;
            }

            return age >= minimumAge;
        });
    }

    private setupRequiredIfProvider(): void {
        this.validationService.addProvider('requiredif', (value, _element, params) => {
            const targetId = params.otherproperty;
            const targetValue = params.otherpropertyvalue;
            const otherPropertyValue = (targetValue ?? '').toString();
            const otherPropertyElement = document.querySelector('#' + targetId) as HTMLInputElement;

            if (otherPropertyElement && otherPropertyElement.checked.toString() === otherPropertyValue.toLowerCase()) {
                return value !== '';
            }

            return true;
        });
    }
}
